import { computed, defineComponent, Prop, PropType } from "vue";
import { OfferJson } from "../OrderForm";
import Utils from "../utils/Utils";
import SmeUtils, { SmeOrderJson } from "../utils/SmeUtils";
import CodeForm from "./CodeForm";
import ReIcon from "./ReIcon";
import { OfferTariff, OfferUi } from "../utils/OfferUtils";

export enum SmeOfferBoxType {
    STANDARD = "standard",
    MINI = "mini",
}

export const SmeSmallBenefit = defineComponent({
    components: {
        ReIcon,
    },

    template: `
        <div class="SmeSmallBenefit" :class="[ format ? 'SmeSmallBenefit--' + format : '' ]">
            <ReIcon :icon="icon" />

            <div v-html="title"></div>
        </div>
    `,

    props: {
        title: { type: String },
        icon: { type: String },
        format: { type: String },
    },
});

export const SmeSmallBenefits = defineComponent({
    components: {
        SmeSmallBenefit,
    },

    template: `
        <div class="SmeSmallBenefits">
            <template v-if="ui">
                <SmeSmallBenefit v-for="item in ui.smallBenefits" :icon="item.icon" :title="item.text" :format="item.format" />
            </template>

            <template v-else>
                <SmeSmallBenefit icon="Trade fee" title="<b>0 zł opłaty handlowej</b> przy e-fakturze i zgodach marketingowych" />
                <SmeSmallBenefit icon="Invoice paid" :title="'<b>' + due_days + '-dniowy</b> termin płatności'" />
                <SmeSmallBenefit icon="House" title="<b>200 zł</b> na ofertę dla domu – bonus dla Ciebie i Twoich pracowników!" />
                <SmeSmallBenefit icon="Leaf" title="<b>100%</b> zielonej energii" />
            </template>
        </div>
    `,

    props: {
        offer: { type: Object as PropType<OfferJson>, required: true },
        ui: { type: Object as PropType<OfferUi> },
    },

    setup(props, ctx) {
        const due_days = computed(() => {
            return SmeUtils.getDueDays(props.offer);
        });

        return {
            due_days,
        }
    },
});

export const SmeBigBenefit = defineComponent({
    components: {
        ReIcon,
    },

    template: `
        <div class="SmeBigBenefit" :class="[ format ? 'SmeSmallBenefit--' + format : '' ]">
            <ReIcon :icon="icon" />

            <div class="SmeBigBenefit__info">
                <div class="SmeBigBenefit__title" v-html="title"></div>
                <div class="SmeBigBenefit__content" v-html="content"></div>
            </div>
        </div>
    `,

    props: {
        title: { type: String },
        content: { type: String },
        icon: { type: String },
        format: { type: String },
    },
});

export const SmeBigBenefits = defineComponent({
    components: {
        SmeBigBenefit,
    },

    template: `
        <div class="SmeBigBenefits">
            <template v-if="ui">
                <SmeBigBenefit v-for="item in ui.largeBenefits" :icon="item.icon" :title="item.text" :format="item.format" />
            </template>

            <template v-else>
                <SmeBigBenefit icon="Shield with Lightning" title="Brak opłaty handlowej" content="Przy wyborze e-faktury i wyrażeniu zgód marketingowych, opłata handlowa wynosi 0 zł!" />
                <SmeBigBenefit icon="Invoice paid" title="Wydłużony termin płatności" :content='Skorzystaj z przedłużonego ' + due_days + '-dniowego terminu płatności dla większej elastyczności!'" />
                <SmeBigBenefit icon="House" title="Zniżka na energię w Twoim domu" content="Otrzymaj 200 zł zniżki na naszą ofertę prądu domu Twojego i twoich prracowników." />
                <SmeBigBenefit icon="Contract for an idefinite period" title="100% zielonej energii" content="I certyfikat" />
            </template>
        </div>
    `,

    props: {
        offer: { type: Object as PropType<OfferJson>, required: true },
        ui: { type: Object as PropType<OfferUi> },
    },

    setup(props, ctx) {
        const due_days = computed(() => {
            return SmeUtils.getDueDays(props.offer);
        });

        return {
            due_days,
        }
    },
});

const SmeOfferBoxPricesValue = defineComponent({
    components: {
        ReIcon,
    },

    template: `
        <div class="SmeOfferBoxPricesValue">
            <ReIcon v-if="!icon_hide" :icon="iconval" />

            <div class="SmeOfferBoxPricesValue__info">
                <b class="SmeOfferBoxPricesValue__amount">{{ priceformat(value, false, 3) }}</b>

                zł/kWh netto

                <div class="SmeOfferBoxPricesValue__postfix" v-if="postfixval" v-html="postfixval"></div>
            </div>
        </div>
    `,

    props: {
        offer: { type: Object as PropType<OfferJson>, required: true },
        type: { type: String, required: true },
        value: { type: Number, required: true },
        postfix: { type: String },

        icon_hide: { type: Boolean, default: false },
    },

    setup(props, ctx) {
        const iconval = computed(() => {
            if (props.offer.tariff == OfferTariff.C12a && props.type == 'day') {
                return "Increase";
            } else if (props.offer.tariff == OfferTariff.C12a && props.type == 'night') {
                return "Decrease";
            } else if (props.offer.tariff == OfferTariff.C12b && props.type == 'day') {
                return "Day";
            } else if (props.offer.tariff == OfferTariff.C12b && props.type == 'night') {
                return "Night";
            } else {
                return null;
            }
        });

        const postfixval = computed(() => {
            if (props.postfix) {
                return props.postfix;
            } else if (props.offer.tariff == OfferTariff.C12a && props.type == 'day') {
                return "w szczycie";
            } else if (props.offer.tariff == OfferTariff.C12a && props.type == 'night') {
                return "poza szczytem";
            } else if (props.offer.tariff == OfferTariff.C12b && props.type == 'day') {
                return "w dzień";
            } else if (props.offer.tariff == OfferTariff.C12b && props.type == 'night') {
                return "w nocy";
            } else {
                return null;
            }
        });

        function priceformat(price, rounded, r = 3) {
            return Utils.priceFormat(price, rounded, '', r);
        }

        return {
            iconval,
            postfixval,

            priceformat,
        };
    },
});

const SmeOfferBoxPrice = defineComponent({
    components: {
        ReIcon,
        SmeOfferBoxPricesValue,
    },

    template: `
        <template v-if="type == 'standard'">
            <template v-if="offer.prices.length == 1">
                <div v-if="!options.savings" class="SmeOfferBox__price">
                    <div>{{ priceformat(offer.prices[0].price_netto) }}</div>

                    <span>zł/kWh <div>netto</div></span>
                </div>

                <div class="SmeOfferBox__fixedprice" :class="{ 'SmeOfferBox__fixedprice--nexts': ui?.prices }">
                    <template v-if="ui?.prices">
                        <ReIcon icon="Shield with Lightning" />

                        <div class="SmeOfferBox__fixedprice-items">
                            <div class="SmeOfferBox__fixedprice-items-price"><span class="SmeOfferBox__fixedprice-items-value"><b>{{ priceformat(offer.prices[0].price_netto) }}</b> zł/kWh</span> netto <span v-if="ui.prices.price1?.postfix" v-html="ui.prices.price1?.postfix"></span></div>
                            <div v-if="ui.prices.next1" class="SmeOfferBox__fixedprice-items-next"><span class="SmeOfferBox__fixedprice-items-value"><b>{{ priceformat(ui.prices.next1?.amount) }}</b> zł/kWh</span> netto <span v-if="ui.prices.next1?.postfix" v-html="ui.prices.next1?.postfix"></span></div>
                        </div>
                    </template>

                    <template v-else-if="ui?.offerGuarantee">
                        <ReIcon :icon="ui.offerGuarantee.icon" />

                        <span>{{ ui.offerGuarantee.text }}</span>
                    </template>

                    <template v-else-if="options.savings">
                        <ReIcon icon="Shield with Lightning" />

                        <span>Stała cena <b>{{ priceformat(offer.prices[0].price_netto) }} zł/kWh</b> netto</span>
                    </template>

                    <template v-else-if="!options.savings">
                        <ReIcon icon="Shield with Lightning" />

                        <span>Gwarancja stałej ceny na <b>{{ yearsTitle(offer.price_warranty) }}</b></span>
                    </template>
                </div>
            </template>

            <template v-else-if="offer.prices.length == 2">
                <div class="SmeOfferBox__prices">
                    <template v-if="ui?.prices">
                        <div class="SmeOfferBox__prices-price">
                            <SmeOfferBoxPricesValue
                                class="SmeOfferBoxPricesValue--price1"
                                :offer="offer"
                                type="day"
                                :value="offer.prices[0].price_netto"
                                :postfix="ui.prices.price1.postfix"
                            />

                            <SmeOfferBoxPricesValue
                                class="SmeOfferBoxPricesValue--next1"
                                v-if="ui.prices.next1"
                                :offer="offer"
                                type="day"
                                :icon_hide="true"
                                :value="ui.prices.next1.amount"
                                :postfix="ui.prices.next1.postfix"
                            />
                        </div>

                        <div class="SmeOfferBox__prices-price">
                            <SmeOfferBoxPricesValue
                                class="SmeOfferBoxPricesValue--price2"
                                :offer="offer"
                                type="night"
                                :value="offer.prices[1].price_netto"
                                :postfix="ui.prices.price2.postfix"
                            />

                            <SmeOfferBoxPricesValue
                                v-if="ui.prices.next2"
                                class="SmeOfferBoxPricesValue--next2"
                                :offer="offer"
                                type="night"
                                :icon_hide="true"
                                :value="ui.prices.next2.amount"
                                :postfix="ui.prices.next2.postfix"
                            />
                        </div>
                    </template>

                    <template v-else-if="!ui?.prices">
                        <div v-for="price,ix in offer.prices" class="SmeOfferBox__prices-price">
                            <SmeOfferBoxPricesValue
                                class="SmeOfferBoxPricesValue--std"
                                :class="[ 'SmeOfferBoxPricesValue--price' + (ix + 1) ]"
                                :offer="offer"
                                :type="price.type"
                                :value="price.price_netto"
                            />
                        </div>
                    </template>
                </div>
            </template>
        </template>

        <template v-else-if="type == 'mini'">
            <template v-if="offer.prices.length == 1">
                <div class="SmeOfferBox__price">
                    <div>{{ priceformat(offer.prices[0].price_netto) }}</div>

                    <span>zł/kWh <div>netto</div></span>
                </div>

                <div class="SmeOfferBox__fixedprice">
                    <ReIcon icon="Shield with Lightning" />

                    <span>Gwarancja stałej ceny na <b>{{ yearsTitle(offer.price_warranty) }}</b></span>
                </div>
            </template>

            <template v-else-if="offer.prices.length == 2">
                <div class="SmeOfferBox__prices">
                    <template v-if="ui?.prices">
                        <div class="SmeOfferBox__prices-price">
                            <SmeOfferBoxPricesValue
                                class="SmeOfferBoxPricesValue--price1"
                                :offer="offer"
                                type="day"
                                :value="offer.prices[0].price_netto"
                                :postfix="ui.prices.price1.postfix"
                            />

                            <SmeOfferBoxPricesValue
                                class="SmeOfferBoxPricesValue--next1"
                                v-if="ui.prices.next1"
                                :offer="offer"
                                type="day"
                                :icon_hide="true"
                                :value="ui.prices.next1.amount"
                                :postfix="ui.prices.next1.postfix"
                            />
                        </div>

                        <div class="SmeOfferBox__prices-price">
                            <SmeOfferBoxPricesValue
                                class="SmeOfferBoxPricesValue--price2"
                                :offer="offer"
                                type="night"
                                :value="offer.prices[1].price_netto"
                                :postfix="ui.prices.price2.postfix"
                            />

                            <SmeOfferBoxPricesValue
                                v-if="ui.prices.next2"
                                class="SmeOfferBoxPricesValue--next2"
                                :offer="offer"
                                type="night"
                                :icon_hide="true"
                                :value="ui.prices.next2.amount"
                                :postfix="ui.prices.next2.postfix"
                            />
                        </div>
                    </template>

                    <template v-else-if="!ui?.prices">
                        <div v-for="price,ix in offer.prices" class="SmeOfferBox__prices-price">
                            <SmeOfferBoxPricesValue
                                class="SmeOfferBoxPricesValue--std"
                                :class="[ 'SmeOfferBoxPricesValue--price' + (ix + 1) ]"
                                :offer="offer"
                                :type="price.type"
                                :value="price.price_netto"
                            />
                        </div>
                    </template>
                </div>

                <div class="SmeOfferBox__fixedprice">
                    <ReIcon icon="Shield with Lightning" />
                    <span>Gwarancja stałej ceny na <b>{{ yearsTitle(offer.price_warranty) }}</b></span>
                </div>
            </template>
        </template>
    `,

    props: {
        options: { type: Object as PropType<SmeOrderJson>, required: true },
        type: { type: String, required: true },
        offer: { type: Object as PropType<OfferJson>, required: true },
        ui: { type: Object as PropType<OfferUi> },
    },

    setup(props, ctx) {
        function yearsTitle(months: number): string {
            return Utils.yearsTitle(months);
        }

        function priceformat(price, rounded, r = 3) {
            return Utils.priceFormat(price, rounded, '', r);
        }

        return {
            yearsTitle,
            priceformat,
        };
    },
});

export default defineComponent({
    components: {
        SmeSmallBenefits,
        CodeForm,
        ReIcon,
        SmeOfferBoxPrice,
    },

    template: `
        <div class="SmeOfferBox" :class="[ options.savings ? 'SmeOfferBox--savings' : 'SmeOfferBox--nsavings' ], { 'SmeOfferBox--code': options.code, 'SmeOfferBox--cashback': offer.cashback } ">
            <div class="SmeOfferBox__inner">
                <div v-if="!options.code || !offer.hidden" class="SmeOfferBox__title">{{ offer.title }}</div>
                <div v-if="options.code && offer.hidden" class="SmeOfferBox__title">{{ options.code.campaignTitle }}</div>

                <div class="SmeOfferBox__body">
                    <template v-if="type == 'standard'">
                        <div v-if="options.savings" class="SmeOfferBox__savings">
                            <div class="SmeOfferBox__savings-title">Szacunkowe oszczędności<sup>*</sup></div>
                            <div class="SmeOfferBox__savings-value">
                                {{ priceformat(options.savings.amount, false, 0) }} <span class="SmeOfferBox__savings-value-unit">zł</span>

                                <div v-if="options.offer.cashback" class="SmeOfferBox__savings-cashback">
                                    +{{ priceformat(options.offer.cashback, false, 0) }} <span>zł</span>
                                </div>
                            </div>

                            <div v-if="!options.code || !offer.hidden" class="SmeOfferBox__savings-discount"><b>{{ options.savings.percent }}% taniej</b> niż na obecnej fakturze!</div>
                        </div>

                        <SmeOfferBoxPrice
                            :options="options"
                            :type="type"
                            :offer="offer"
                            :ui="offer_ui"
                        />

                        <div v-if="!options.savings && options.code && options.offer.cashback" class="SmeOfferBox__code-cashback">
                            +{{ priceformat(options.offer.cashback, false, 0) }} <span>zł</span>
                        </div>

                        <div class="SmeOfferBox__select">
                            <button type="button" class="button button--primary button--full" @click="$emit('select')">
                                <template v-if="options.savings">Zacznij oszczędzać!</template>
                                <template v-else>Wybierz</template>
                            </button>
                        </div>

                        <SmeSmallBenefits
                            :offer="offer"
                            :ui="offer_ui"
                        />

                        <hr>

                        <template v-if="options.code">
                            <div class="SmeOfferBox__code">
                                <div class="SmeOfferBox__code-inner">
                                    <ReIcon icon="Checkmark" />
                                    <span>Kod <b>{{ options.code.code }}</b> aktywny</span>

                                    <button type="button" class="SmeOfferBox__code-remove" @click="onCodeCancel">
                                        <ReIcon icon="Close" />
                                    </button>
                                </div>

                                <div v-if="!options.offer.hidden && label_code_notice" class="SmeOfferBox__code-notice" v-html="label_code_notice"></div>
                            </div>
                        </template>

                        <template v-if="!options.code">
                            <CodeForm
                                type="single"
                                :sme="true"
                                :offer="offer"
                                :code="options.code"

                                @loading="$emit('code:loading')"
                                @applyoptions="onCodeApply"
                            />
                        </template>
                    </template>

                    <template v-if="type == 'mini'">
                        <SmeOfferBoxPrice
                            :options="options"
                            :type="type"
                            :offer="offer"
                            :ui="offer_ui"
                        />

                        <hr class="SmeOfferBox__hr" />
                    </template>

                    <slot></slot>
                </div>
            </div>
        </div>
    `,

    emits: [
        'select',
        'code:loading',
        'code:change',
    ],

    props: {
        options: { type: Object as PropType<SmeOrderJson>, required: true },
        offer: { type: Object as PropType<OfferJson>, required: true },

        type: { type: String as PropType<SmeOfferBoxType>, default: 'standard' },

        label_code_notice: { type: String },
    },

    setup(props, ctx) {
        const offer_ui = computed(() => {
            if (!props.options.code) {
                return null;
            }

            for (const item of props.options.code.items) {
                if (item.offer.id == props.offer.id) {
                    return item.ui;
                }
            }

            return null;
        });

        function yearsTitle(months: number): string {
            return Utils.yearsTitle(months);
        }

        function priceformat(price, rounded, r = 3) {
            return Utils.priceFormat(price, rounded, '', r);
        }

        function onCodeApply(options: SmeOrderJson) {
            Utils.setStep('sme.offer', options.offer);

            ctx.emit('code:change');
        }

        async function  onCodeCancel() {
            await Utils.setCart('sme/code', {
                type: 'single',
                code: null,
                offer: props.offer?.id,
            });

            ctx.emit('code:change');
        }

        return {
            offer_ui,

            yearsTitle,
            priceformat,
            onCodeApply,
            onCodeCancel,
        };
    },
});
